import { ComplianceEntitiesSortField } from "~/graphql/supergraphEnums";
import { toGlobalId } from "~/utils/graphql-helpers";
import type { OnboardingCaseType } from "../types";
import type { CaseInfoFormValues } from "./schema";

export const UNLINKED_ENTITY_OPTION_ID = "__unlinked_entity__";
export const UNLINKED_CLIENT_OPTION_ID = "__unlinked_client__";

const toRelayId = (objectType: string, raw: string | null): string | undefined => {
  if (!raw) return undefined;
  return /^\d+$/.test(raw) ? toGlobalId(objectType, raw) : raw;
};

export const entityNameSliceFilter = (entityName: string) => ({
  field: ComplianceEntitiesSortField.EntityName,
  filterValues: [entityName],
});

export const findEntitySliceRow = <T extends { id: number }>(
  rows: readonly T[] | null | undefined,
  entityDbId: number,
): T | undefined => rows?.find((row) => row.id === entityDbId);

export const findClientSliceRow = <T extends { id: number }>(
  rows: readonly T[] | null | undefined,
  arenaDbId: number,
): T | undefined => findEntitySliceRow(rows, arenaDbId);

export const caseInfoValuesFromSearch = (
  search: string,
  caseType: OnboardingCaseType,
): Pick<CaseInfoFormValues, "clientId" | "clientName" | "entityId" | "entityName"> => {
  const params = new URLSearchParams(search);
  return {
    clientId: toRelayId("ArenaObjectType", params.get("clientId")),
    clientName: params.get("clientName") ?? undefined,
    entityId:
      caseType === "entity"
        ? toRelayId("AccountObjectType", params.get("entityId"))
        : undefined,
    entityName: caseType === "entity" ? (params.get("entityName") ?? undefined) : undefined,
  };
};

export const canContinueCaseInfo = (
  values: Pick<
    CaseInfoFormValues,
    "clientId" | "clientName" | "entityId" | "entityName" | "isUnlinked"
  >,
  caseType: OnboardingCaseType,
): boolean => {
  if (caseType === "client") {
    return (
      Boolean(values.clientId) || (values.isUnlinked && Boolean(values.clientName?.trim()))
    );
  }
  if (!values.clientId) return false;
  return Boolean(values.entityId) || (values.isUnlinked && Boolean(values.entityName?.trim()));
};

export const isLinkedOnboardingSubject = (
  values: Pick<CaseInfoFormValues, "clientId" | "entityId" | "isUnlinked">,
  caseType: OnboardingCaseType,
): boolean => {
  if (values.isUnlinked) return false;
  if (caseType === "client") return Boolean(values.clientId);
  return Boolean(values.entityId);
};

export const unlinkedChipLabel = (caseType: OnboardingCaseType): string =>
  caseType === "client" ? "Unlinked client onboarding case" : "Unlinked entity onboarding case";

export const caseInfoPrompt = (caseType: OnboardingCaseType): string =>
  caseType === "client"
    ? "Which client is associated with this onboarding case?"
    : "Which client and entity is associated with this onboarding case?";

export const programSectionCopy = (caseType: OnboardingCaseType, clientName: string): string =>
  caseType === "client"
    ? "Configure the compliance program this client must comply with when onboarding on to the Juniper Square platform."
    : `Configure the compliance program ${clientName || "this client"} must comply with when onboarding this unlinked entity on to the Juniper Square platform.`;
